import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, pdf, Link } from '@react-pdf/renderer';
import logoAgs from '../../assets/logo-ags.png';
import clipButtonImg from '../../assets/naranja_neutral_con_sombra.png';

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Times-Roman',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
  },
  contentContainer: {
    paddingLeft: '0.75in',
    paddingRight: '0.75in',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  headerAccent: {
    height: '8px',
    width: '100%',
    backgroundColor: '#002542',
  },
  footerAccent: {
    height: '8px',
    width: '100%',
    backgroundColor: '#00a941',
    marginTop: '10px',
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: '15px',
  },
  bodyContainer: {
    flex: 1,
  },
  leftHeader: {
    width: '40%',
  },
  rightHeader: {
    width: '40%',
    textAlign: 'right',
    marginTop: '45px',
  },
  contactText: {
    fontSize: '12px',
    color: '#666666',
    marginBottom: '3px',
  },
  websiteText: {
    fontSize: '12px',
    fontWeight: 'semibold',
    color: '#00a941',
    marginBottom: '3px',
  },
  dateBadge: {
    backgroundColor: '#002542',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'medium',
    textAlign: 'right',
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#002542',
    textAlign: 'center',
    marginBottom: '15px',
    marginTop: '5px',
  },
  bankInfoContainer: {
    marginBottom: '15px',
  },
  bankInfoRow: {
    flexDirection: 'row',
    marginBottom: '6px',
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: '4px',
  },
  bankInfoLabel: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#002542',
    width: '30%',
  },
  bankInfoValue: {
    fontSize: '12px',
    color: '#333333',
    width: '70%',
  },
  noteContainer: {
    border: '2px solid #002542',
    padding: '12px',
    marginBottom: '15px',
    backgroundColor: '#f8f9fa',
  },
  noteText: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#002542',
    textAlign: 'center',
    lineHeight: 1.4,
  },
  jugadorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '10px',
  },
  jugadorLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#002542',
    marginRight: '10px',
  },
  jugadorValue: {
    fontSize: '14px',
    color: '#333333',
    fontWeight: 'medium',
  },
  importeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '20px',
  },
  importeLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#002542',
    marginRight: '10px',
  },
  importeValue: {
    fontSize: '14px',
    color: '#333333',
    fontWeight: 'medium',
  },
  tableContainer: {
    marginBottom: '15px',
  },
  table: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#002542',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableHeader: {
    backgroundColor: '#002542',
    color: 'white',
    fontSize: '12px',
    fontWeight: 'bold',
    padding: '7px',
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: '#002542',
  },
  tableCell: {
    fontSize: '12px',
    padding: '7px',
    borderRightWidth: 1,
    borderRightColor: '#e0e0e0',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    flex: 1,
    textAlign: 'center',
  },
  tableCellLast: {
    fontSize: '12px',
    padding: '7px',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    flex: 1,
  },
  importantNote: {
    fontSize: '10px',
    color: '#d32f2f',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  totalLabel: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'center',
  },
  totalValue: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'center',
  },
  finalNoteContainer: {
    border: '2px solid #d32f2f',
    padding: '10px',
    marginTop: '5px',
    backgroundColor: '#fff5f5',
  },
  finalNoteText: {
    fontSize: '7px',
    color: '#d32f2f',
    fontWeight: 'bold',
    textAlign: 'justify',
    lineHeight: 1.3,
  },
  clipContainer: {
    marginTop: '10px',
    alignItems: 'center',
  },
  clipButtonImage: {
    width: '240px',
    height: 'auto',
  },
  clipHint: {
    fontSize: '9px',
    color: '#666666',
    marginTop: '4px',
    textAlign: 'center',
  },
});

const FichaDeposito = ({ 
  jugador, 
  gira,
  jugadorRef,
  giraImporte,
  wagrImporte = 0,
  cijImporte = 0,
  descuentoReferencias = 0,
  banco = '', 
  cuenta = '', 
  sucursal = '', 
  clabe = '', 
  beneficiario = '',
  fecha = new Date().toLocaleDateString('es-MX'),
  clipUrl = null,
}) => {
  // Nota: @react-pdf/renderer es más estable con imágenes PNG/JPG que con SVG remotos.
  const CLIP_BUTTON_IMG = clipButtonImg;

  const totalBase = (giraImporte || 0) + (wagrImporte || 0) + (cijImporte || 0);
  const totalConDescuento = Math.max(0, totalBase - (descuentoReferencias || 0));

  const MyDocument = () => (
    <Document>
      <Page size="LETTER" style={styles.page}>
        {/* Header accent bar */}
        <View style={styles.headerAccent} />

        {/* Content Container */}
        <View style={styles.contentContainer}>
          {/* Header with left and right sections */}
          <View style={styles.headerContainer}>
            <View style={styles.leftHeader}>
              <Image
                src={logoAgs}
                style={{ width: '100px', height: 'auto', marginBottom: '5px', marginTop: '35px' }}
              />
            </View>

            <View style={styles.rightHeader}>
              <Text style={styles.contactText}>Email: info@golfsur.com</Text>
              <Text style={styles.websiteText}>www.golfsur.com.mx</Text>
              <View style={[styles.dateBadge, { width: '80px', marginLeft: 'auto' }]}>
                <Text>{fecha}</Text>
              </View>
            </View>
          </View>

          {/* Title */}
          <Text style={styles.title}>Ficha de Deposito</Text>

          {/* Bank Information */}
          <View style={styles.bankInfoContainer}>
            <View style={styles.bankInfoRow}>
              <Text style={styles.bankInfoLabel}>Banco:</Text>
              <Text style={styles.bankInfoValue}>{banco}</Text>
            </View>
            <View style={styles.bankInfoRow}>
              <Text style={styles.bankInfoLabel}>Cuenta:</Text>
              <Text style={styles.bankInfoValue}>{cuenta}</Text>
            </View>
            <View style={styles.bankInfoRow}>
              <Text style={styles.bankInfoLabel}>Sucursal:</Text>
              <Text style={styles.bankInfoValue}>{sucursal}</Text>
            </View>
            <View style={styles.bankInfoRow}>
              <Text style={styles.bankInfoLabel}>CLABE:</Text>
              <Text style={styles.bankInfoValue}>{clabe}</Text>
            </View>
            <View style={styles.bankInfoRow}>
              <Text style={styles.bankInfoLabel}>Beneficiario:</Text>
              <Text style={styles.bankInfoValue}>{beneficiario}</Text>
            </View>
          </View>

          {/* Note with borders */}
          <View style={styles.noteContainer}>
            <Text style={styles.noteText}>
              ESTIMADO(A) CAJERO(A): FAVOR DE EFECTUAR UN DEPÓSITO O TRANSFERENCIA ELECTRÓNICA POR CADA IMPORTE CON SU RESPECTIVA REFERENCIA
            </Text>
          </View>

          {/* Jugador field */}
          <View style={styles.jugadorContainer}>
            <Text style={styles.jugadorLabel}>Jugador:</Text>
            <Text style={styles.jugadorValue}>{jugador}</Text>
          </View>

          {/* Importe Table */}
          <View style={styles.tableContainer}>
            <View style={styles.table}>
              {/* Header Row */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableHeader, { flex: 2 }]}>Inscripción</Text>
                <Text style={[styles.tableHeader, { flex: 1 }]}>Referencia</Text>
                <Text style={[styles.tableHeader, { flex: 1 }]}>Importe</Text>
              </View>
              
              {/* Data Rows - only show if value > 0 */}
              {giraImporte > 0 && (
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 2 }]}>{gira}</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>{jugadorRef}</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>${giraImporte}</Text>
                </View>
              )}
              
              {wagrImporte > 0 && (
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 2 }]}>Importe WAGR</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>{jugadorRef}</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>${wagrImporte}</Text>
                </View>
              )}
              
              {cijImporte > 0 && (
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 2 }]}>Importe CIJ</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>{jugadorRef}</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>${cijImporte}</Text>
                </View>
              )}
              
              {descuentoReferencias > 0 && (
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 2 }]}>Descuento por Referencias</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]} />
                  <Text style={[styles.tableCell, { flex: 1 }]}>-${descuentoReferencias}</Text>
                </View>
              )}
              
              {/* Total Row */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.importantNote, { flex: 2 }]}>
                  IMPORTANTE: INDICAR LA REFERENCIA EN LA DESCRIPCIÓN DEL PAGO
                </Text>
                <Text style={[styles.tableCell, styles.totalLabel, { flex: 1 }]}>TOTAL:</Text>
                <Text style={[styles.tableCellLast, styles.totalValue, { flex: 1 }]}>${totalConDescuento}</Text>
              </View>
            </View>
          </View>

          {/* Final Note */}
          <View style={styles.finalNoteContainer}>
            <Text style={styles.finalNoteText}>
              Todo aquel jugador que no ponga el número de referencia al momento de efectuar depósito o transferencia bancaría, será penalizado
              con $500.00 M.N. mismos que tendrá que depositar con el mismo número de referencia que se le proporciono en la ficha de inscripción.
              Sin haber cubierto esta penalización no se podrá participar, aún cuando haya sido pagada la inscripción pero no se haya
              identificado el pago, esta penalización se aplicará sin excepción o excusa alguna, evítenos la pena de decirle que antes hubiese leído las
              instrucciones descriptas en la convocatoria.
            </Text>
          </View>

          {/* Clip payment button (optional) */}
          {clipUrl ? (
            <View style={styles.clipContainer}>
              <Link src={clipUrl}>
                <Image src={CLIP_BUTTON_IMG} style={styles.clipButtonImage} />
              </Link>
              <Text style={styles.clipHint}>También puedes pagar en línea con Clip.</Text>
            </View>
          ) : null}
        </View>

        {/* Footer accent bar */}
        <View style={styles.footerAccent} />
      </Page>
    </Document>
  );

  // Generate PDF and return blob
  const generatePDF = async () => {
    const pdfBlob = await pdf(<MyDocument />).toBlob();
    return pdfBlob;
  };

  return generatePDF();
};

export default FichaDeposito;
